import * as Cookies from "es-cookie";

/**
 * One place to end a session and send the user back to login.
 *
 * Before this, every page did its own version: ~50 components check
 * `Cookies.get("studenttoken") == undefined` in render() and push to
 * /student/login, and each Navbar's logout removes its own hand-written list of
 * cookies. Neither covers the case that actually matters here — the token
 * cookie is present but the server rejects it (expired, revoked, or malformed),
 * which the API now reports as an UNAUTHENTICATED error. The page-level checks
 * see a token, decide the user is logged in, and render a screen whose every
 * query has already failed.
 *
 * `errorLink` in src/index.js calls this on the first such error, so an invalid
 * token lands on login instead of an empty page full of "Server Error" alerts.
 */

const LOGIN_PATH = "/student/login";

/**
 * Paths that must not be redirected away from, lowercased for comparison.
 * Bouncing to login from the login page would reload in a loop if a query there
 * ever failed with UNAUTHENTICATED. Registration and password reset are here for
 * the same reason: they run logged out and must be allowed to finish.
 */
const LOGIN_PATHS = [
  "/login",
  "/clogin",
  "/student/login",
  "/student/logineamcet",
  "/student/register",
  "/student/forgotpassword",
  "/vignan/login"
];

/** localStorage keys written during login; cookies are cleared wholesale below. */
const SESSION_STORAGE_KEYS = [
  "profile_pic",
  "studentglobals",
  "globalsubjects",
  "packageplan",
  "homemodal",
  "loginrefermodal",
  "loginfoundationModal"
];

/**
 * Removes every cookie rather than a fixed list. The list approach is what the
 * existing logout handlers do and it has already drifted — login writes
 * institution_id, user_valid, student_user_exam_id and branch_id, and no logout
 * removes any of them, so a stale institution survives into the next session.
 * Cookies here are all session state; there is nothing worth preserving across
 * a forced logout.
 */
export function clearSession() {
  const all = Cookies.getAll();
  Object.keys(all).forEach(name => Cookies.remove(name));

  SESSION_STORAGE_KEYS.forEach(key => {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Safari private mode throws on localStorage access; the cookies are
      // already gone, which is what decides whether the user is logged in.
    }
  });
}

export function isOnLoginPage() {
  const path = (window.location.pathname || "").toLowerCase();
  return LOGIN_PATHS.some(p => path.indexOf(p) === 0);
}

/**
 * Guards against a burst of redirects: a single screen fires a dozen queries and
 * all of them fail together, so without this each one would trigger its own
 * navigation.
 */
let redirecting = false;

/**
 * `location.assign`, not react-router's history: this is called from an Apollo
 * link, outside the component tree, where no history object is in scope. The
 * full reload is wanted anyway — it drops the InMemoryCache, so none of the
 * previous session's data is left for the next user of the browser.
 */
export function redirectToLogin() {
  if (redirecting || isOnLoginPage()) return;
  redirecting = true;
  silenceAlerts();
  window.location.assign(LOGIN_PATH);
}

/**
 * Navigation is not instant — React keeps rendering until the new document
 * arrives — and ~50 components respond to a failed query with
 * `alert("Server Error. " + error.message)` straight out of render(). Each alert
 * is modal and blocks the unload, so without this the user has to dismiss a
 * dozen "Authorization header not" dialogs before the login page appears. The
 * override only lives for the moment between deciding to redirect and the page
 * being replaced.
 */
function silenceAlerts() {
  window.alert = function () {};
}

/** Clear credentials and bounce to login. Safe to call from many places at once. */
export function endSessionAndRedirect() {
  if (redirecting || isOnLoginPage()) return;
  clearSession();
  redirectToLogin();
}

export default endSessionAndRedirect;
